import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatUiDate, formatUiMonthYear, parseUiDateInput } from '../../utils/uiFormat';

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_OPTIONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const getDateFromIso = (value) => {
  if (!value) return null;
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getIsoDate = (date) => (
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
);

const buildMonthCells = (viewDate) => {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDayOffset = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = Array(firstDayOffset).fill(null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(year, month, day));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

const UiDateField = ({
  value,
  onChange,
  className = '',
  inputClassName = '',
  min,
  max,
  placeholder = 'Mon, 30 May 2026',
  disabled = false,
}) => {
  const containerRef = useRef(null);
  const selectedDate = useMemo(() => getDateFromIso(value), [value]);
  const formattedValue = useMemo(() => (value ? formatUiDate(value) : ''), [value]);
  const [draft, setDraft] = useState(formattedValue);
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => selectedDate || new Date());
  const [openLeft, setOpenLeft] = useState(false);

  useEffect(() => {
    setDraft(formattedValue);
  }, [formattedValue]);

  useEffect(() => {
    if (selectedDate) {
      setViewDate(selectedDate);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (!open || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const calendarWidth = 320;
    const willOverflowLeft = rect.right - calendarWidth < 8;
    setOpenLeft(willOverflowLeft);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        setDraft(formattedValue);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, formattedValue]);

  const minDate = useMemo(() => getDateFromIso(min), [min]);
  const maxDate = useMemo(() => getDateFromIso(max), [max]);

  const monthCells = useMemo(() => buildMonthCells(viewDate), [viewDate]);
  const yearOptions = useMemo(() => {
    const anchorYear = (selectedDate || new Date()).getFullYear();
    return Array.from({ length: 31 }, (_, index) => anchorYear - 15 + index);
  }, [selectedDate]);

  const isDisabledDate = (date) => {
    const iso = getIsoDate(date);
    if (minDate && iso < getIsoDate(minDate)) return true;
    if (maxDate && iso > getIsoDate(maxDate)) return true;
    return false;
  };

  const applyDate = (nextDate) => {
    const iso = getIsoDate(nextDate);
    onChange?.(iso);
    setDraft(formatUiDate(iso));
    setViewDate(nextDate);
    setOpen(false);
  };

  const commitValue = (rawValue) => {
    const parsed = parseUiDateInput(rawValue);
    if (parsed) {
      const parsedDate = getDateFromIso(parsed);
      if (parsedDate && !isDisabledDate(parsedDate)) {
        onChange?.(parsed);
        setDraft(formatUiDate(parsed));
        setViewDate(parsedDate);
        return;
      }
    }
    setDraft(formattedValue);
  };

  return (
    <div ref={containerRef} className={`relative flex items-center gap-2 ${className}`}>
      <input
        type="text"
        value={draft}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => {
          if (!containerRef.current?.contains(e.relatedTarget)) {
            commitValue(e.target.value);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commitValue(draft);
            setOpen(false);
            e.currentTarget.blur();
          }
          if (e.key === 'Escape') {
            setDraft(formattedValue);
            setOpen(false);
            e.currentTarget.blur();
          }
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
          }
        }}
        onFocus={(e) => e.currentTarget.select()}
        className={`w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 ${inputClassName}`}
      />
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className="rounded-xl border border-gray-200 bg-white p-2 text-gray-500 shadow-sm transition hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400"
        aria-label="Pick date"
      >
        <CalendarDays size={18} />
      </button>

      {open && !disabled && (
        <div className={`absolute top-full z-[90] mt-2 w-[20rem] rounded-2xl border border-gray-200 bg-white p-3 shadow-xl ${openLeft ? 'left-0' : 'right-0'}`}>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
              className="rounded-lg border border-gray-200 p-2 text-gray-500 hover:border-primary-300 hover:text-primary-600"
              aria-label="Previous month"
            >
              <ChevronLeft size={16} />
            </button>
            <select
              value={viewDate.getMonth()}
              onChange={(e) => setViewDate((prev) => new Date(prev.getFullYear(), Number(e.target.value), 1))}
              className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
            >
              {MONTH_OPTIONS.map((monthLabel, index) => (
                <option key={monthLabel} value={index}>{monthLabel}</option>
              ))}
            </select>
            <select
              value={viewDate.getFullYear()}
              onChange={(e) => setViewDate((prev) => new Date(Number(e.target.value), prev.getMonth(), 1))}
              className="w-[6.25rem] rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
              className="rounded-lg border border-gray-200 p-2 text-gray-500 hover:border-primary-300 hover:text-primary-600"
              aria-label="Next month"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <div className="mt-3 text-center text-xs font-medium text-gray-500">
            {formatUiMonthYear(getIsoDate(viewDate))}
          </div>

          <div className="mt-3 grid grid-cols-7 gap-1 text-center">
            {WEEKDAY_LABELS.map((label) => (
              <div key={label} className="py-1 text-[11px] font-semibold text-gray-400">
                {label}
              </div>
            ))}
          </div>

          <div className="mt-1 grid grid-cols-7 gap-1">
            {monthCells.map((cellDate, index) => {
              if (!cellDate) {
                return <div key={`empty-${index}`} className="h-9 rounded-lg bg-gray-50" />;
              }

              const iso = getIsoDate(cellDate);
              const isSelected = value === iso;
              const isToday = iso === getIsoDate(new Date());
              const isDisabled = isDisabledDate(cellDate);

              return (
                <button
                  key={iso}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => applyDate(cellDate)}
                  className={`h-9 rounded-lg text-sm transition ${
                    isSelected
                      ? 'bg-primary-600 text-white'
                      : isDisabled
                        ? 'cursor-not-allowed bg-gray-50 text-gray-300'
                        : isToday
                          ? 'border border-primary-300 bg-primary-50 text-primary-700 hover:bg-primary-100'
                          : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {cellDate.getDate()}
                </button>
              );
            })}
          </div>

          <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
            <button
              type="button"
              onClick={() => {
                if (!minDate || !isDisabledDate(new Date())) {
                  applyDate(new Date());
                }
              }}
              className="text-xs font-medium text-primary-600 hover:text-primary-700"
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => {
                onChange?.('');
                setDraft('');
                setOpen(false);
              }}
              className="text-xs font-medium text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UiDateField;
